import { useState, useEffect, useMemo } from 'react'
import { v4 as uuid } from 'uuid'

type PreviousSearches = {
  id: string
  displayName: string
}

export const usePreviousSearches = () => {
  const STORAGE_KEY = 'gsPreviousSearches'
  const MAX_SEARCHES = 5

  const [previousSearches, setPreviousSearchesState] = useState<
    PreviousSearches[]
  >([])

  const fetchPreviousSearches = () => {
    const searches = localStorage.getItem(STORAGE_KEY)
    setPreviousSearchesState(searches ? JSON.parse(searches) : [])
  }

  // On mount, load initial data from localStorage
  useEffect(() => {
    fetchPreviousSearches()
  }, [])

  const setPreviousSearch = (search: string) => {
    if (!search.length) return

    const existingSearches = JSON.parse(
      localStorage.getItem(STORAGE_KEY) || '[]'
    ) as PreviousSearches[]

    const currentSearches = existingSearches.filter(
      (x) => x.displayName !== search
    )

    if (currentSearches.length >= MAX_SEARCHES) {
      currentSearches.shift() // Remove the oldest search
    }

    const newSearch = {
      id: uuid(),
      displayName: search,
    }

    const newSearches = [...currentSearches, newSearch]

    // Save to localStorage and update state
    localStorage.setItem(STORAGE_KEY, JSON.stringify(newSearches))
    setPreviousSearchesState(newSearches)

    // Fetch the updated searches
    fetchPreviousSearches()
  }

  const handleClear = (id: string) => {
    const existingSearches = JSON.parse(
      localStorage.getItem(STORAGE_KEY) || '[]'
    ) as PreviousSearches[]

    const filteredSearches = existingSearches.filter(
      (search) => search.id !== id
    )

    localStorage.setItem(STORAGE_KEY, JSON.stringify(filteredSearches))
    setPreviousSearchesState(filteredSearches)
  }

  const searches = useMemo(
    () => previousSearches?.reverse(),
    [previousSearches]
  )

  return {
    searches,
    previousSearches,
    setPreviousSearch,
    fetchPreviousSearches,
    handleClear,
  }
}
